import type { SyncState } from "../../infrastructure/sync/automerge.ts";

export interface SyncStatusCapabilities {
  document: Document;
  status(): { state: SyncState; label: string };
  failureReason?(): string | null;
  pendingChanges(): Promise<number>;
  retry(): Promise<void>;
  openAccount(): Promise<void>;
  icon(name: string): string;
}

const explanations: Record<SyncState, string> = {
  synced: "This device is up to date with the server.",
  local: "Your changes are saved on this device and waiting to reach the server.",
  syncing: "This device is exchanging changes with the server.",
  offline: "Your changes stay saved on this device. Synchronization resumes when you reconnect.",
  attention: "Synchronization could not finish. Your pending changes remain saved on this device. Try again, or review the account recovery options.",
};

export function createSyncStatusController(capabilities: SyncStatusCapabilities) {
  const { document } = capabilities;
  let active: HTMLDialogElement | null = null;

  async function open(): Promise<void> {
    if (active?.isConnected) return;
    const previous = document.activeElement as HTMLElement | null;
    const previousMenuTrigger = previous?.closest("details")?.querySelector<HTMLElement>("summary");
    const dialog = document.createElement("dialog");
    active = dialog;
    dialog.className = "name-dialog sync-status-dialog";
    dialog.setAttribute("aria-labelledby", "sync-status-title");
    dialog.innerHTML = `<header class="ui-dialog__header"><h2 id="sync-status-title">Synchronization</h2><button type="button" class="icon-button" data-sync-close aria-label="Close synchronization status">${capabilities.icon("close")}</button></header><div class="sync-status-body"><p class="sync-status-current" data-sync-current role="status"></p><p data-sync-explanation></p><p class="muted" data-sync-pending>Reading pending changes…</p><p class="notice notice--error" data-sync-error role="alert" hidden></p></div><footer class="dialog-actions"><button type="button" class="button button--soft" data-sync-account>Account & recovery</button><button type="button" class="button button--primary" data-sync-retry>Try syncing again</button></footer>`;
    const retry = dialog.querySelector<HTMLButtonElement>("[data-sync-retry]")!;
    const error = dialog.querySelector<HTMLElement>("[data-sync-error]")!;
    let retrying = false;
    let openingAccount = false;

    async function refresh(): Promise<void> {
      const { state, label } = capabilities.status();
      const reason = capabilities.failureReason?.();
      if (state === "attention" && reason) { error.textContent = `Server response: ${reason}`; error.hidden = false; }
      else if (state === "synced" || state === "syncing") error.hidden = true;
      const current = dialog.querySelector<HTMLElement>("[data-sync-current]")!;
      if (current.textContent !== label) current.textContent = label;
      dialog.querySelector<HTMLElement>("[data-sync-explanation]")!.textContent = explanations[state];
      retry.disabled = retrying || state === "syncing";
      try {
        const pending = await capabilities.pendingChanges();
        dialog.querySelector<HTMLElement>("[data-sync-pending]")!.textContent = pending ? `${pending} ${pending === 1 ? "change is" : "changes are"} waiting to synchronize.` : "No pending changes on this device.";
      } catch {
        dialog.querySelector<HTMLElement>("[data-sync-pending]")!.textContent = "Pending changes could not be read. Try reopening this dialog.";
      }
    }

    dialog.querySelector("[data-sync-close]")!.addEventListener("click", () => dialog.close());
    dialog.addEventListener("click", event => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener("close", () => {
      clearInterval(timer);
      dialog.remove();
      if (active === dialog) active = null;
      if (openingAccount) return;
      if (previousMenuTrigger?.isConnected) previousMenuTrigger.focus({ preventScroll: true });
      else if (previous?.isConnected) previous.focus({ preventScroll: true });
      else document.querySelector<HTMLElement>('[data-action="sync-status"]')?.focus({ preventScroll: true });
    }, { once: true });
    dialog.querySelector("[data-sync-account]")!.addEventListener("click", () => { openingAccount = true; dialog.close(); void capabilities.openAccount(); });
    retry.addEventListener("click", async () => {
      if (retrying) return;
      retrying = true; retry.disabled = true; error.hidden = true;
      try { await capabilities.retry(); }
      catch { error.textContent = "Synchronization could not finish. Your pending changes remain on this device."; error.hidden = false; }
      finally { retrying = false; if (dialog.isConnected) await refresh(); }
    });
    document.body.append(dialog);
    dialog.showModal();
    dialog.querySelector<HTMLElement>("[data-sync-close]")!.focus();
    const timer = setInterval(() => { void refresh(); }, 1000);
    await refresh();
  }

  return { open };
}
